<h1 mat-dialog-title>How to install Inbox Pro Web Chat widget on a website</h1>

<mat-dialog-content>
  <h2>Install the WordPress web chat plugin</h2>
  <ol>
    <li>Download your custom plugin from Inbox Pro Settings &gt; Web Chat &gt; Installation &gt; WordPress</li>
    <li>
      Go to
      <i>your-website.com</i>
      /wp-admin &gt; Plugins &gt; Add New Plugin
    </li>
    <li>Upload the zip file containing your plugin, and install it.</li>
    <li>Activate it, and you’ll see the chat appear on your site after reloading the page.</li>
  </ol>

  <h2>Alternatively, install the website embed code</h2>
  <p>
    To install your web chat widget on your website, copy and paste your install code into the
    <code class="code-tag">&lt;head&gt;</code>
    element of your website, typically just before the closing
    <code class="code-tag">&lt;/head&gt;</code>
    tag.
  </p>

  <h3>For a WordPress site with Divi theme</h3>
  <ol>
    <li>Copy your web chat installation code into your clipboard.</li>
    <li>
      Navigate to your WordPress backend admin, usually at
      <i>your-website.com</i>
      /wp-admin
    </li>
    <li>Go to Divi &gt; Theme Options &gt; Integrations</li>
    <li>
      Paste the code into the section titled "Add code to the
      <code class="code-tag">&lt;head&gt;</code>
      of your blog."
    </li>
    <li>Save changes. Your web chat widget should now be live on your website, on all pages.</li>
  </ol>

  <h3>For a WordPress site without Divi theme</h3>
  <p>
    To save custom code to the header of your website, you’ll need to download a plugin like
    <a href="https://wordpress.org/plugins/header-footer-code-manager/" target="_blank" rel="noopener noreferrer">
      Header Footer Code Manager.
    </a>
    (We are not affiliated with these plugins, use at your own risk.)
  </p>
  <ol>
    <li>Copy your web chat installation code to your clipboard.</li>
    <li>
      Go to your WordPress backend admin, typically at
      <i>your-website.com</i>
      /wp-admin
    </li>
    <li>Navigate to Plugins &gt; Add New</li>
    <li>Search for your plugin, Install and Activate it.</li>
    <li>
      Once the plugin is downloaded and activated, navigate to the area in the plugin where you can add custom content
      to the
      <code class="code-tag">&lt;head&gt;</code>
      element of your website, and paste your web chat code.
    </li>
  </ol>

  <mat-divider></mat-divider>

  <h2>Common issues</h2>

  <h3>Issue: I cannot scroll, or type the 'space' key, or a certain character into the chat widget</h3>
  <p>
    Some plugin features can interfere with the ability to type in the widget because they “hijack” that key for their
    own functions. Try disabling plugins, one by one, to find the plugin that’s affecting the web chat.
  </p>

  <h3>Issue: I don't see the web chat on my site, even though it’s installed in the right place</h3>
  <p>
    If the Web chat code is installed on your website, and enabled in Inbox Settings, and you cannot see it on your
    website, the most likely next cause is usually cache or plugin issues. You can try additional steps on your website
    to clear cache or any blocking plugins to get the web chat to appear.
  </p>

  <h3>Caching issues</h3>
  <p>
    Most websites use multiple types of caches that help the site to load faster. You’ll want to reset the caches, to
    get your latest website changes to appear.
  </p>
  <ol>
    <li>Browser cache – to tell your browser to load the website without using local device cache</li>
    <ol>
      <li>Shift+command+R (Mac)</li>
      <li>Ctrl + F5 (PC)</li>
    </ol>
    <li>
      Website hosting cache – clear the cache that Website product manages, by clicking the ‘Flush Cache’ button on the
      Website overview page.
    </li>
    <li>
      Divi CSS cache – Disable the Static CSS File Generation cache in your Divi Theme options &gt; Builder &gt;
      Advanced
    </li>
    <li>
      Other Cache plugins – Ensure there are no other cache plugins activated on your website; if so, disable them.
    </li>
  </ol>

  <h3>Google reCAPTCHA badge is in the way</h3>
  <p>
    When reCAPTCHA is being used on a website form, this badge will appear in the bottom right of a webpage – which can
    be in the way of the web chat widget.
  </p>
  <p>
    You can hide the badge completely on your website with some custom CSS; just make sure to follow Google’s required
    alternative ways to inform the user that reCAPTCHA is being used. Read more:
    <a
      href="https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed"
      target="_blank"
      rel="noopener noreferrer"
    >
      https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed
    </a>
  </p>

  <p>To hide the reCAPTCHA badge, go to your website CSS editor,</p>
  <ol>
    <li>Log in to your WordPress dashboard</li>
    <li>
      Go to
      <b>Appearance &gt; Customizer</b>
    </li>
    <li>
      Click
      <b>Additional CSS</b>
    </li>
    <li>
      Add the snippet below:
      <pre><code>.grecaptcha-badge {{'{'}}
  visibility: hidden!important;
{{'}'}}</code></pre>
    </li>
    <li>Save and preview your website.</li>
  </ol>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-flat-button mat-dialog-close color="primary">Close</button>
</mat-dialog-actions>
